import { useCallback, useEffect, useRef, useState } from 'react';
import { TIKTOK_ID_PATTERN } from 'layouts/auth';
import { checkTiktokId } from './register-api';
import type { TiktokAvailability } from './register-api';

export type TiktokCheckStatus = 'idle' | 'checking' | TiktokAvailability;

export interface TiktokCheckState {
  status: TiktokCheckStatus;
  /** Giá trị mà `status` nói tới (đã chuẩn hoá). */
  value: string;
}

const DEBOUNCE_MS = 400;

/**
 * Kiểm tra TikTok ID còn trống khi đang gõ (GET users/check, như app — app debounce 500ms,
 * đặc tả §4 chọn 400ms). Chỉ gọi khi đúng định dạng; kết quả "còn trống / đã có người dùng"
 * được nhớ theo từng giá trị để gõ qua gõ lại không gọi lại. Lỗi mạng không nhớ (lần sau thử lại).
 * Throttle backend là 20 request / 20 giây theo IP nên debounce là bắt buộc.
 */
export function useTiktokCheck() {
  const cache = useRef(new Map<string, Exclude<TiktokAvailability, 'error'>>());
  const latest = useRef('');
  const timer = useRef<number>();
  const [state, setState] = useState<TiktokCheckState>({ status: 'idle', value: '' });

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const run = useCallback(async (value: string): Promise<TiktokAvailability> => {
    const cached = cache.current.get(value);
    if (cached) return cached;
    const result = await checkTiktokId(value);
    if (result !== 'error') cache.current.set(value, result);
    return result;
  }, []);

  /** Gọi mỗi khi ô TikTok ID đổi giá trị. */
  const schedule = useCallback(
    (value: string) => {
      window.clearTimeout(timer.current);
      latest.current = value;
      if (!value || !TIKTOK_ID_PATTERN.test(value)) {
        setState({ status: 'idle', value });
        return;
      }
      const cached = cache.current.get(value);
      if (cached) {
        setState({ status: cached, value });
        return;
      }
      // Chưa hiện "Đang kiểm tra…" khi còn đang gõ, để chữ dưới ô không nhấp nháy.
      setState({ status: 'idle', value });
      timer.current = window.setTimeout(async () => {
        if (latest.current !== value) return;
        setState({ status: 'checking', value });
        const result = await run(value);
        if (latest.current === value) setState({ status: result, value });
      }, DEBOUNCE_MS);
    },
    [run]
  );

  /** Chờ kết quả cho `value` ngay (bấm Tạo tài khoản khi lượt kiểm tra chưa xong). */
  const ensure = useCallback(
    async (value: string): Promise<TiktokAvailability> => {
      window.clearTimeout(timer.current);
      latest.current = value;
      const cached = cache.current.get(value);
      if (cached) {
        setState({ status: cached, value });
        return cached;
      }
      setState({ status: 'checking', value });
      const result = await run(value);
      if (latest.current === value) setState({ status: result, value });
      return result;
    },
    [run]
  );

  /** Backend báo 409 'Tiktok ID already exists' lúc đăng ký: nhớ lại để ô báo lỗi ngay. */
  const markTaken = useCallback((value: string) => {
    cache.current.set(value, 'taken');
    if (latest.current === value) setState({ status: 'taken', value });
  }, []);

  const isTaken = useCallback((value: string) => cache.current.get(value) === 'taken', []);

  return { state, schedule, ensure, markTaken, isTaken };
}
